/**
 * The creator agreement's plain text, read back into the structure it was
 * drafted with so it can be typeset rather than dumped.
 *
 * The body is generated from counsel's Word file, one block per blank-line
 * separated paragraph, and it keeps that file's conventions: ALL-CAPS
 * headings, numbered sections written `01   Title`, and lettered clauses
 * written `(a)<tab>…`. Only the layout changes. Every block's text is the
 * source text exactly, because this is what the applicant signs: reading it
 * back out must give the same words in the same order.
 */
export type AgreementBlock =
  | { kind: "heading"; text: string }
  | { kind: "title"; text: string }
  | { kind: "section"; number: string; text: string }
  | { kind: "clause"; marker: string; text: string }
  | { kind: "paragraph"; text: string };

const SECTION = /^(\d{2})\s+(.+)$/s;
const CLAUSE = /^(\([a-z]\))\s+(.+)$/s;

function isHeading(text: string): boolean {
  return /[A-Z]/.test(text) && text === text.toUpperCase();
}

export function parseAgreement(raw: string): AgreementBlock[] {
  const blocks: AgreementBlock[] = [];
  let seenTitle = false;
  for (const chunk of raw.split(/\n\s*\n/)) {
    const text = chunk.trim();
    if (!text) continue;
    const previous = blocks.at(-1);
    const section = SECTION.exec(text);
    const clause = CLAUSE.exec(text);
    if (isHeading(text)) {
      blocks.push({ kind: "heading", text });
    } else if (section) {
      blocks.push({ kind: "section", number: section[1], text: section[2] });
    } else if (clause) {
      blocks.push({ kind: "clause", marker: clause[1], text: clause[2] });
    } else if (
      // The document's name, and the name under each PART: both are the
      // first mixed-case line after a heading that introduces a title.
      !seenTitle ||
      (previous?.kind === "heading" && previous.text.startsWith("PART "))
    ) {
      seenTitle = true;
      blocks.push({ kind: "title", text });
    } else {
      blocks.push({ kind: "paragraph", text });
    }
  }
  return blocks;
}

/** The words of a block, marker included, in reading order. */
export function blockWords(block: AgreementBlock): string {
  if (block.kind === "section") return `${block.number} ${block.text}`;
  if (block.kind === "clause") return `${block.marker} ${block.text}`;
  return block.text;
}
